import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";
import { createPaymentPlatformAssessment } from "../api/companyApi";

const STEPS = ["Business", "Application", "Performance", "Review"];
const INITIAL_FORM = {
  externalId: "", applicationId: "", companyName: "", registrationNumber: "",
  website: "", email: "", city: "", state: "", zip: "",
  requestedAmount: "", averageMonthlyVolume: "", businessMonths: "", fico: "", businessRisk: "",
  transactions: "", disputes: "", logins: "", productViews: "", feedbackScore: "", feedbackCount: "",
  entityConfidence: "", legitimacy: "", externalRisk: "",
};

const numeric = (value) => value === "" || value == null ? null : Number(value);
const money = (value) => value == null ? "—" : `$${Number(value).toLocaleString()}`;
const sentence = (value) => String(value || "").replaceAll("_", " ");
const fieldSx = { minWidth: 0 };

function Field({ form, field, set, ...props }) {
  return <TextField size="small" fullWidth sx={fieldSx} value={form[field]} onChange={(event) => set(field, event.target.value)} {...props} />;
}

function FieldGrid({ children, columns = 2 }) {
  return <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `repeat(${columns}, minmax(0, 1fr))` }, gap: 1.5 }}>{children}</Box>;
}

function ReviewSection({ title, rows }) {
  const populated = rows.filter(([, value]) => value !== "" && value != null);
  if (!populated.length) return null;
  return (
    <Box sx={{ p: 2, border: "1px solid #e3ebf3", borderRadius: 2.5 }}>
      <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: 1 }}>{title}</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
        {populated.map(([label, value]) => (
          <Box key={label}>
            <Typography sx={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ color: "#17233f", overflowWrap: "anywhere" }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function PaymentPlatformAddCompanyFlow({ tenantId, open, onClose, onCreated, onOpenReport }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (open) return;
    setForm(INITIAL_FORM);
    setStep(0);
    setResult(null);
    setError("");
    setBusy(false);
  }, [open]);

  const hasIdentifier = useMemo(() => [form.externalId, form.companyName, form.website, form.registrationNumber, form.email, form.applicationId].some((value) => value.trim()), [form]);
  const stepError = step === 0 && !hasIdentifier
    ? "Enter at least one reliable business identifier."
    : step === 1 && (!numeric(form.requestedAmount) || numeric(form.requestedAmount) <= 0)
      ? "Enter the amount the business is requesting."
      : "";

  const next = () => {
    if (stepError) { setError(stepError); return; }
    setError("");
    setStep((current) => Math.min(STEPS.length - 1, current + 1));
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await createPaymentPlatformAssessment(tenantId, {
        identity: {
          externalId: form.externalId, applicationId: form.applicationId, companyName: form.companyName,
          website: form.website, registrationNumber: form.registrationNumber, email: form.email,
          city: form.city, state: form.state, zip: form.zip,
        },
        internalSignals: {
          fico: numeric(form.fico), businessRisk: numeric(form.businessRisk), businessMonths: numeric(form.businessMonths),
          averageMonthlyVolume: numeric(form.averageMonthlyVolume), disputes: numeric(form.disputes), transactions: numeric(form.transactions),
          logins: numeric(form.logins), productViews: numeric(form.productViews), feedbackScore: numeric(form.feedbackScore),
          feedbackCount: numeric(form.feedbackCount), entityConfidence: form.entityConfidence, legitimacy: form.legitimacy,
          externalRisk: form.externalRisk,
        },
        requestedAmount: numeric(form.requestedAmount),
      });
      setResult(data);
      onCreated?.(data);
      toast.success("MCA assessment is ready");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "The assessment could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { if (!busy) onClose?.(); };
  const assessment = result?.assessment;
  const hasOffer =
    assessment?.decision === "approve" &&
    assessment?.recommendation?.amount != null;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" aria-labelledby="mca-add-company-title">
      <DialogTitle id="mca-add-company-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "#eaf2ff", color: "#1e6fe8", display: "grid", placeItems: "center" }}><AddBusinessIcon /></Box>
          <Box>
            <Typography sx={{ fontSize: 21, fontWeight: 900, color: "#0b2a4a" }}>Add company for MCA assessment</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b" }}>Enter what you know. CrediArc resolves the entity, combines available evidence, and runs your MCA model.</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 475 }}>
        {!result && <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>{STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}</Stepper>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {busy && <Box sx={{ mb: 2 }}><LinearProgress /><Typography sx={{ color: "#64748b", fontSize: 13, mt: 1 }}>Resolving identity, aggregating available evidence, and running the MCA model…</Typography></Box>}

        {!result && step === 0 && <>
          <Alert severity="info" sx={{ mb: 2 }}>Only one identifier is required. Add more when available to improve match confidence.</Alert>
          <FieldGrid>
            <Field form={form} field="companyName" set={set} label="Legal or trading name" autoFocus />
            <Field form={form} field="externalId" set={set} label="Merchant / account ID" />
            <Field form={form} field="registrationNumber" set={set} label="Registration number / EIN" />
            <Field form={form} field="applicationId" set={set} label="Application ID" />
            <Field form={form} field="website" set={set} label="Website" placeholder="example.com" />
            <Field form={form} field="email" set={set} label="Business email" type="email" />
            <Field form={form} field="city" set={set} label="City" />
            <Field form={form} field="state" set={set} label="State / region" />
            <Field form={form} field="zip" set={set} label="ZIP / postal code" />
          </FieldGrid>
        </>}

        {!result && step === 1 && <>
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Financing request</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>Requested amount is required. Revenue and maturity improve offer sizing and eligibility checks.</Typography>
          <FieldGrid>
            <Field form={form} field="requestedAmount" set={set} label="Requested amount" type="number" required InputProps={{ startAdornment: <Box component="span" sx={{ mr: .5 }}>$</Box> }} />
            <Field form={form} field="averageMonthlyVolume" set={set} label="Average monthly payment volume" type="number" InputProps={{ startAdornment: <Box component="span" sx={{ mr: .5 }}>$</Box> }} />
            <Field form={form} field="businessMonths" set={set} label="Months in business" type="number" />
            <Field form={form} field="fico" set={set} label="Principal FICO score" type="number" inputProps={{ min: 300, max: 850 }} />
            <Field form={form} field="businessRisk" set={set} label="Internal business risk (0–100)" type="number" inputProps={{ min: 0, max: 100 }} />
          </FieldGrid>
        </>}

        {!result && step === 2 && <>
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: .5 }}>Payment-platform performance</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>All fields are optional. They are combined with any evidence already held for the matched merchant.</Typography>
          <FieldGrid columns={3}>
            <Field form={form} field="transactions" set={set} label="Transactions" type="number" />
            <Field form={form} field="disputes" set={set} label="Disputes / chargebacks" type="number" />
            <Field form={form} field="logins" set={set} label="Platform logins" type="number" />
            <Field form={form} field="productViews" set={set} label="Product views" type="number" />
            <Field form={form} field="feedbackScore" set={set} label="Feedback score" type="number" />
            <Field form={form} field="feedbackCount" set={set} label="Feedback count" type="number" />
            <Field select form={form} field="entityConfidence" set={set} label="Identity confidence"><MenuItem value="">Not provided</MenuItem><MenuItem value="verified">Verified</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></Field>
            <Field select form={form} field="legitimacy" set={set} label="Business verification"><MenuItem value="">Not provided</MenuItem><MenuItem value="verified">Verified</MenuItem><MenuItem value="corroborated">Corroborated</MenuItem><MenuItem value="unverified">Unverified</MenuItem></Field>
            <Field select form={form} field="externalRisk" set={set} label="Additional risk indicator"><MenuItem value="">Not provided</MenuItem><MenuItem value="low">Low</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="critical">Critical</MenuItem></Field>
          </FieldGrid>
        </>}

        {!result && step === 3 && <Box sx={{ display: "grid", gap: 1.5 }}>
          <Alert severity="info">On assessment, CrediArc will match this entity, combine the submitted values with available merchant evidence, apply guardrails, and produce a recommendation.</Alert>
          <ReviewSection title="Business" rows={[["Name", form.companyName], ["Merchant ID", form.externalId], ["Registration / EIN", form.registrationNumber], ["Website", form.website], ["Application", form.applicationId], ["Location", [form.city, form.state, form.zip].filter(Boolean).join(", ")]]} />
          <ReviewSection title="Application" rows={[["Requested", money(numeric(form.requestedAmount))], ["Monthly volume", money(numeric(form.averageMonthlyVolume))], ["Months in business", form.businessMonths], ["FICO", form.fico], ["Internal risk", form.businessRisk]]} />
          <ReviewSection title="Performance" rows={[["Transactions", form.transactions], ["Disputes", form.disputes], ["Logins", form.logins], ["Product views", form.productViews], ["Identity confidence", form.entityConfidence], ["Verification", form.legitimacy]]} />
        </Box>}

        {result && <Box>
          <Box sx={{ textAlign: "center", py: 1 }}>
            <CheckCircleRoundedIcon sx={{ color: "#12a150", fontSize: 52 }} />
            <Typography sx={{ fontWeight: 900, fontSize: 24, color: "#0b2a4a" }}>Assessment ready</Typography>
            <Typography sx={{ color: "#64748b" }}>{result.aggregation?.matchedExistingEntity ? "Existing merchant evidence was combined with the new application." : "The new company was resolved and assessed."}</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.25 }}>
            {[["Decision", sentence(assessment?.decision)], ["Score", assessment?.score ?? "—"], ["Evidence", `${assessment?.coverage || 0}%`], ["Offer", hasOffer ? money(assessment?.recommendation?.amount) : "No offer"]].map(([label, value]) => <Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f7f9fc" }}><Typography sx={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", fontWeight: 800 }}>{label}</Typography><Typography sx={{ color: "#0b2a4a", fontWeight: 900, fontSize: 19 }}>{value}</Typography></Box>)}
          </Box>
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {hasOffer && assessment?.recommendation?.termMonths && <Chip label={`${assessment.recommendation.termMonths} month term`} />}
            {hasOffer && assessment?.recommendation?.factorRate && <Chip label={`${assessment.recommendation.factorRate} factor rate`} />}
            <Chip label={`${sentence(assessment?.recommendation?.riskBand)} risk`} />
            {assessment?.recommendation?.requiresManualReview && <Chip color="warning" label="Manual review required" />}
          </Box>
          {!hasOffer && <Alert severity="warning" sx={{ mt: 2 }}>No commercial offer was generated. Complete the required review before taking a funding action.</Alert>}
          {!!assessment?.missingFeatures?.length && <Alert severity="warning" sx={{ mt: 2 }}>Next step: collect {assessment.missingFeatures.slice(0, 3).map(sentence).join(", ")}{assessment.missingFeatures.length > 3 ? " and additional missing evidence" : ""} before re-running the assessment.</Alert>}
        </Box>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {result ? <>
          <Button onClick={close}>Done</Button>
          <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => onOpenReport?.(result.id || result.entityId)}>Open full report</Button>
        </> : <>
          <Button onClick={step === 0 ? close : () => { setError(""); setStep((current) => current - 1); }} startIcon={step > 0 ? <ArrowBackIcon /> : undefined}>{step === 0 ? "Cancel" : "Back"}</Button>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ color: "#64748b", fontSize: 12, mr: 1 }}>Step {step + 1} of {STEPS.length}</Typography>
          {step < STEPS.length - 1 ? <Button variant="contained" onClick={next} endIcon={<ArrowForwardIcon />}>Continue</Button> : <Button variant="contained" disabled={busy} onClick={submit} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddBusinessIcon />}>{busy ? "Assessing…" : "Aggregate & assess"}</Button>}
        </>}
      </DialogActions>
    </Dialog>
  );
}
